import React from 'react';
import { ArrowRight } from '../../components/Glyph';
import ListGrid from '../../components/ListGrid';
import { Pill } from '../admin/adminUtils';
import { pressable } from '../../lib/pressable';

/*
  The pieces the two cycle screens are both built from.

  Written 5 September 2026, building Group C's Cycle Details after Inventory
  Counts. **The two screens are the same drawing with a different grouping.** An
  admin's Inventory Counts groups the cycle's locations by region; a manager's
  Cycle Details groups the same locations by rep. Everything else on them is the
  same thing: the strip of live numbers, the cards, the panel that opens under a
  card, and the list of locations inside it.

  They were two separate programs, and each of those was itself written twice,
  once for a desk and once for below 1024. **Four copies of one screen**, which
  is the exact fault this job exists to end: a change that has to be made in
  four places will be made in four places every time, forever, and these had
  already drifted.

  What is NOT shared, and should not be: what each screen SAYS. An admin's card
  is a region and carries Review Hub and Close cycle; a manager's card is a
  person and carries neither, because a manager cannot close a cycle. The
  buttons on a row differ too, because an admin may edit somebody else's count
  and a manager may only look at it.
*/

// -- The cycle's four numbers -------------------------------------------------

// The four things a location's count can be, in the order the strip shows them.
// Named once because the numbers are drawn in two shapes: a row of labelled
// numbers beside the cycle's name on a desk, and a strip of four cells below
// 768. Group C's approved drawings show both.
// `dot` is for a dot or a bar; `ink` is for the words, because the dot colours
// are too pale to read as words (amber measured 1.9 on white, theme phase 6).
export const CYCLE_STATS = [
  { key: 'not_started', label: 'Not started', dot: 'var(--text-muted)', ink: 'var(--text-secondary)' },
  // In progress blue, Submitted gold: the brand guide's 11.4, rebrand step 3.
  { key: 'in_progress', label: 'In progress', dot: 'var(--info)',       ink: 'var(--info-ink)' },
  { key: 'submitted',   label: 'Submitted',   dot: 'var(--accent)',     ink: 'var(--accent-ink)' },
  { key: 'approved',    label: 'Approved',    dot: 'var(--success)',    ink: 'var(--success-ink)' },
];

/*
  One live number on the cycle strip: the figure, then what it is, side by side.

  **Rule 5: the numbers sit on hairlines rather than in a card**, which is what
  Group C's drawings show, and it is why these screens open on the locations
  rather than on a summary of them.

  **A status number is also a filter**, which the card it replaced did too and
  the drawing does not show: tapping one narrows the list, tapping it again
  clears it. Taking that away would have been a change the drawing never asked
  for. A number with nothing to filter by is a plain span rather than a button,
  because a button that does nothing is worse than no button.
*/
export function CycleStat({ n, label, dot, on, onClick }) {
  const body = (
    <>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, flexShrink: 0 }} />
      <b style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>{n}</b>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{label}</span>
    </>
  );
  /*
    **`cycle-figure` carries the tap floor (rule 13).** Measured at 24 pixels
    tall in the running app on 7 September 2026, on every screen that shows the
    four numbers, and these are filters a person taps. The class does nothing on
    a desk, where a mouse is precise.
  */
  const style = {
    display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
    border: 'none', background: 'transparent', font: 'inherit', padding: '2px 0',
  };
  if (!onClick) return <span style={style}>{body}</span>;
  return (
    <button type="button" onClick={onClick} title="Show only these" className="cycle-figure"
      style={{ ...style, cursor: 'pointer', borderBottom: on ? '2px solid ' + dot : '2px solid transparent' }}>
      {body}
    </button>
  );
}

/*
  The same four numbers as a strip of cells, which is what the drawings show
  below 768. The row above runs off the right-hand edge of a phone, and it did:
  found at 390 on 5 September 2026 with "68 Not started" the last thing on
  screen and the other three not there at all.
*/
export function CycleStrip({ stats, filter, onFilter }) {
  return (
    <span className="cycle-strip">
      {CYCLE_STATS.map(c => (
        <button key={c.key} type="button"
          className={'cycle-cell' + (filter === c.key ? ' on' : '')}
          style={{ borderBottomColor: filter === c.key ? c.dot : 'transparent' }}
          onClick={() => onFilter(filter === c.key ? 'all' : c.key)}>
          <b>{stats[c.key]}</b>
          <span>{c.label}</span>
        </button>
      ))}
    </span>
  );
}

// -- A location on a row ------------------------------------------------------

/*
  A location's name, with its territory above it and its state down the side.

  Group C's drawings show both: the four-letter prefix a rep actually uses to
  talk about a place ("DFW"), and a coloured bar in the four count colours, so a
  person scanning sixty rows sees where the work is without reading a word.

  **The prefix is not always there**, and a location with none simply has none:
  inventing a placeholder would be inventing a fact about that location.

  `withRep` prints the rep under the name, for the middle shape of the list where
  the Rep column has been dropped. The stylesheet decides when that is, so this
  is always in the page and drawn only between 640 and 1023.
*/
export function LocationName({ p, withRep }) {
  const bar = STATE_COLOUR[p.status] || 'var(--text-muted)';
  const prefix = p.warehouse?.region_prefix;
  return (
    <span style={{ display: 'flex', alignItems: 'stretch', gap: 11, minWidth: 0 }}>
      <span style={{ width: 3, borderRadius: 2, background: bar, flexShrink: 0 }} />
      <span style={{ minWidth: 0 }}>
        {prefix && (
          <span style={{ display: 'block', fontSize: 9, fontWeight: 800, letterSpacing: 1.4, color: 'var(--text-secondary)' }}>{prefix}</span>
        )}
        {/* **The clean name, not the raw one.** A location is stored as
            "DFW-MESA VERDE SPORTS MEDICINE" and the prefix is already on the
            line above, so the raw name prints it twice. `display_name` is that
            name with the prefix taken off, which the database has been deriving
            since August and which is what the drawings show. */}
        <span style={{ display: 'block', fontWeight: 700 }}>{p.warehouse?.display_name || p.warehouse?.name}</span>
        {withRep && (
          <span className="lgrid-only-mid" style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 1 }}>
            {repNames(p) || <span style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>Nobody assigned</span>}
          </span>
        )}
      </span>
    </span>
  );
}

// The people on a location, as one string, or empty where there are none.
export function repNames(p) {
  return (p.allReps || []).filter(Boolean).map(r => r.full_name).join(', ');
}

// The name a person reads on a row, which is the clean one. Used for sorting,
// so a list comes out in the order it looks like it is in.
export function locationName(p) {
  return p.warehouse?.display_name || p.warehouse?.name || '';
}

/*
  How much of a sheet is filled in, as "34 / 45" over a thin bar.

  **The drawings show this where the app had a money figure**, and the reason is
  what a manager or an administrator is actually chasing: not what a location is
  worth, but how much of it is still blank. The money is on the card and on the
  cycle strip, where a total belongs.

  **"No lines yet" rather than "0 / 0"** (rule 9): a sheet with nothing on it has
  not been counted as nothing, it has not been opened.
*/
export function LinesDone({ n }) {
  if (!n || !n.total) return <span style={{ color: 'var(--text-secondary)' }}>No lines yet</span>;
  const done = Math.min(n.done, n.total);
  const pct  = Math.round(done / n.total * 100);
  return (
    <span style={{ display: 'block', minWidth: 0 }}>
      <span style={{ fontSize: 12.5 }}>
        <b style={{ fontWeight: 800 }}>{done}</b>
        <span style={{ color: 'var(--text-secondary)' }}> / {n.total}</span>
      </span>
      <span style={{ display: 'block', height: 3, borderRadius: 2, background: 'var(--border-soft)', marginTop: 4, maxWidth: 90, overflow: 'hidden' }}>
        <span style={{ display: 'block', height: '100%', width: pct + '%', background: pct === 100 ? 'var(--success)' : 'var(--accent)' }} />
      </span>
    </span>
  );
}

/*
  The button at the end of a row.

  **Three tones, and the drawings decide which**: a count still to be done gets
  a filled one, because that is the act a person came to do; a count already
  handed in gets a quiet one, because looking at it is not the same kind of act;
  and a count somebody else is in the middle of gets the gold one, because
  opening it is a thing to think about first.

  The shape is here so the two screens cannot drift on it, and they had:
  Cycle Details wrote "View Count" and "Continue Count" in title case where
  Inventory Counts wrote them in sentence case, and used a pale blue where the
  drawing shows white. **What each button DOES is still each screen's own
  business**, because an admin may edit somebody else's count and a manager may
  only look at it.
*/
const ROW_TONES = {
  primary: { color: 'var(--on-action)', background: 'var(--action)' },
  quiet:   { color: 'var(--text-secondary)', background: 'var(--surface-glass)', boxShadow: 'inset 0 0 0 1px var(--border)' },
  caution: { color: 'var(--accent-ink)', background: 'var(--accent-bg)', boxShadow: 'inset 0 0 0 1px var(--accent)' },
};

export function RowButton({ tone = 'primary', onClick, title, children }) {
  return (
    <button type="button" title={title} className="row-btn"
      onClick={e => { e.stopPropagation(); onClick(); }}
      style={{
        fontSize: 12, fontWeight: 700, padding: '8px 14px', borderRadius: 'var(--oh-radius-chip)',
        minWidth: 118, textAlign: 'center', boxSizing: 'border-box',
        whiteSpace: 'nowrap', fontFamily: 'inherit', cursor: 'pointer', border: 'none',
        ...ROW_TONES[tone],
      }}>
      {children}
    </button>
  );
}

/*
  How much of a sheet is filled in, on a card rather than in a list row: the same
  two numbers with the bar running out beside them to the card's edge.

  Group C's approved drawing of My Counts puts it here, in place of the money
  figure that was on the card before, for the same reason the list rows carry it:
  what a rep wants from their own card is how much is left, not what it is worth.
*/
export function LinesOnCard({ n }) {
  if (!n || !n.lines) return <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>No lines on this sheet yet</span>;
  const done = Math.min(n.counted, n.lines);
  const pct  = Math.round(done / n.lines * 100);
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <span style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
        <b style={{ fontWeight: 800 }}>{done}</b>
        <span style={{ color: 'var(--text-secondary)' }}> / {n.lines} lines</span>
      </span>
      <span style={{ flex: 1, height: 3, borderRadius: 2, background: 'var(--border-soft)', overflow: 'hidden', minWidth: 30 }}>
        <span style={{ display: 'block', height: '100%', width: pct + '%', background: pct === 100 ? 'var(--success)' : 'var(--accent)' }} />
      </span>
    </span>
  );
}

// The colour of the bar down a row's or a card's left edge, by what state the
// count is in. One place, because it is on three screens now.
export const STATE_COLOUR = {
  not_started: 'var(--text-muted)', in_progress: 'var(--info)',
  submitted: 'var(--accent)', approved: 'var(--success)',
};

// -- A card for one group, region or rep --------------------------------------

/*
  One card. A region on Inventory Counts, a person on Cycle Details, and the
  drawing is the same card either way.

  `kind` is the small grey word above the name, and it earns its place on
  exactly one card: **Unassigned is not a region and not a person**, it is every
  location nobody covers, and a row of cards that all look alike says otherwise.
*/
export function GroupCard({ kind, name, total, value, stats, open, onClick }) {
  const pct = total > 0 ? Math.round((stats.approved + stats.submitted) / total * 100) : 0;
  const legend = CYCLE_STATS
    .map(c => ({ n: stats[c.key], c: c.dot, ink: c.ink, label: c.label }))
    .filter(l => l.n > 0);
  const seg = (n, first, last) => ({
    width: (n / total * 100) + '%',
    borderRadius: first && last ? 99 : first ? '4px 0 0 4px' : last ? '0 4px 4px 0' : 0,
  });
  const shown = CYCLE_STATS.filter(c => stats[c.key] > 0);
  return (
    <div {...pressable(onClick)}
      style={{ background: 'var(--surface-card)', border: '1px solid var(--border)', borderRadius: 'var(--oh-radius-card)', boxShadow: 'var(--depth-rest)', padding: '18px 20px', cursor: 'pointer', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 3 }}>{kind}</div>
          <div className="oh-title" style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-strong)' }}>{name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            {total + ' location' + (total !== 1 ? 's' : '') + (value ? ' - ' + value + ' est. value' : '')}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Done</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--action-text)' }}>{pct}%</div>
        </div>
      </div>
      {total > 0 && (
        <>
          <div style={{ display: 'flex', height: 8, borderRadius: 'var(--oh-radius-pill)', overflow: 'hidden', gap: 2 }}>
            {shown.map((c, i) => (
              <div key={c.key} style={{ background: c.dot, ...seg(stats[c.key], i === 0, i === shown.length - 1) }} />
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 7 }}>
            {legend.map(l => (
              <span key={l.label} style={{ fontSize: 10.5, fontWeight: 700, color: l.ink, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: l.c }} />
                {l.n + ' ' + l.label}
              </span>
            ))}
          </div>
        </>
      )}
      <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid var(--border-soft)', fontSize: 12, fontWeight: 700, color: 'var(--link)' }}>
        {open ? 'Hide locations' : <>View locations <ArrowRight size={13} /></>}
      </div>
    </div>
  );
}

// How many cards sit side by side, from Group C's approved drawings: three on a
// desk and on a tablet on its side, two on a tablet upright, one on anything
// narrower. Counted here rather than left to the stylesheet, because the
// locations of an opened card are laid UNDER THE ROW that card is in, so the
// number of cards in a row is something the screen has to know.
export function cardColumns(isDesktop, isTwoUp) {
  return isDesktop ? 3 : isTwoUp ? 2 : 1;
}

// -- The panel that opens under a card ----------------------------------------

/*
  The locations of one group, as a list.

  `showRep` is the one difference between the two screens: a region holds
  locations belonging to several reps and has to name them, and a person's own
  panel does not, because every row in it belongs to them.

  Three shapes, which is rule 15's own note: every column on a desk, the columns
  a narrower screen can spare dropped between 640 and 1023 with the rep moved
  under the location's name, and a block on a phone.
*/
export function LocationList({ rows, lineCounts, showRep, action, dayOf, empty }) {
  const columns = [
    // The drawings give the location half the row: it is the longest thing on
    // the line and the only one a person reads to find their place in the list.
    { label: 'Location', width: showRep ? '4.4fr' : '4.4fr', midWidth: '2.6fr' },
    ...(showRep ? [{ label: 'Rep', width: '1.3fr', hideMid: true }] : []),
    { label: 'Lines', width: '1.1fr', midWidth: '1.2fr' },
    { label: 'Status', width: '1fr', midWidth: '1.1fr' },
    { label: 'Submitted', width: '0.9fr', hideMid: true },
    // No heading over the buttons, which is what the drawings show. A column
    // still has to exist for it or rule 15's count of headings and cells breaks.
    { label: '', width: '1.25fr', midWidth: '1.5fr', align: 'right' },
  ];
  return (
    <ListGrid
      columns={columns}
      rows={rows}
      keyOf={p => p.id}
      empty={empty}
      cells={p => [
        <LocationName p={p} withRep={showRep} />,
        ...(showRep ? [
          repNames(p)
            ? <span style={{ color: 'var(--text-secondary)' }}>{repNames(p)}</span>
            : <span style={{ color: 'var(--critical-ink)', fontSize: 12, fontWeight: 700 }}>Nobody assigned</span>,
        ] : []),
        <LinesDone n={lineCounts[p.id]} />,
        <Pill status={p.status} />,
        // **Words, not a dash.** The drawings put a dash here, and Group C's own
        // decision of 5 September 2026 was to take dashes out of these cells:
        // one stood for three different things at once and a person could not
        // tell which. Rule 9, blank is not zero. The departure is written down
        // in docs/design/group-c/README.md.
        <span style={{ color: 'var(--text-secondary)' }}>{p.submitted_at ? dayOf(p.submitted_at) : 'Not submitted'}</span>,
        action(p),
      ]}
      /*
        The narrow shape the drawings ask for: the territory and the name, then
        the status and the rep together on one line, then how much of the sheet
        is done. Six labelled lines became three, and a row that was 190 pixels
        tall on a phone is about 100.

        **The button stays out**, which is where this parts company with the
        drawings: they show no button at all and leave tapping the row as the way
        in, and rule 33 says the act a person came to do is never behind
        something.
      */
      stack={p => (
        <>
          <LocationName p={p} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 7 }}>
            <Pill status={p.status} />
            {showRep && (repNames(p)
              ? <span style={{ color: 'var(--text-secondary)', fontSize: 12.5 }}>{repNames(p)}</span>
              : <span style={{ color: 'var(--critical-ink)', fontSize: 12, fontWeight: 700 }}>Nobody assigned</span>)}
            {p.submitted_at && <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{dayOf(p.submitted_at)}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 8 }}>
            <LinesDone n={lineCounts[p.id]} />
            {action(p)}
          </div>
        </>
      )}
    />
  );
}

/*
  The header of an opened panel: one quiet line naming the group and how many
  locations are in it.

  It used to be a title block with the word Region above the name and a Hide
  button opposite. Both were saying something already on the screen: the card
  directly above says which group this is, and it reads "Hide locations" when
  it is open, so a second Hide was a second door onto the same room.
*/
export function PanelHead({ name, count }) {
  return (
    <div style={{ background: 'var(--surface-data)', padding: '13px 20px', borderBottom: '1px solid var(--border-soft)', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase', color: 'var(--text-primary)' }}>{name}</span>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        {count + ' location' + (count !== 1 ? 's' : '')}
      </span>
    </div>
  );
}

/*
  The cards, in rows, with an opened panel laid under the row its card is in.

  `groups` is [{ key, kind, name, rows }] in the order they should appear.
  Everything else about how a group is summarised is worked out here, from its
  rows, so the two screens cannot disagree about what "50% done" means.
*/
export function GroupGrid({ groups, columns, openKey, onToggle, valueOf, renderPanel }) {
  const rows = [];
  for (let i = 0; i < groups.length; i += columns) rows.push(groups.slice(i, i + columns));
  return rows.map((row, ri) => {
    const openInRow = row.find(g => g.key === openKey);
    return (
      <div key={'group-row-' + ri}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(' + columns + ', minmax(0, 1fr))', gap: 14, marginBottom: 14 }}>
          {row.map(g => (
            <GroupCard
              key={g.key}
              kind={g.kind}
              name={g.name}
              total={g.rows.length}
              value={valueOf ? valueOf(g.rows) : null}
              stats={countStates(g.rows)}
              open={g.key === openKey}
              onClick={() => onToggle(g.key)}
            />
          ))}
        </div>
        {openInRow && renderPanel(openInRow)}
      </div>
    );
  });
}

// How many of a set of locations are in each of the four states.
export function countStates(rows) {
  const out = { not_started: 0, in_progress: 0, submitted: 0, approved: 0 };
  for (const p of rows) if (out[p.status] !== undefined) out[p.status] += 1;
  return out;
}
